import { useCallback, useEffect, useRef, useState } from 'react';
import { askSol } from '../../data/sol/sol-client';
import {
  loadSolConversationMessages,
  type SolRestoredMessage,
} from '../../data/queries/use-sol-conversations';
import { reportError } from '../../lib/monitoring';
import { presentSolResult } from './present-sol-result';
import { useSolSession } from './sol-session';
import type { SolExchange } from './sol-exchange';

export type { SolExchange } from './sol-exchange';

/**
 * ONE ASK AT A TIME, AND THE RIGHT ANSWER UNDER THE RIGHT QUESTION.
 * M9 / S09-04.
 *
 * **THE DEFECT THIS EXISTS TO PREVENT IS SPECIFIC AND SILENT.** Brandon asks
 * one question, waits, gives up, and asks another. The first request comes back
 * LAST. Without a sequence, the screen renders the first answer under the
 * second question, and there is nothing on it to say so — a wrong answer that
 * looks exactly like a right one. So every ask carries a monotonic id, and
 * **only the newest id may write state.** A stale reply is dropped: not shown,
 * not queued, not filed into the history.
 *
 * **AND THE OLD REQUEST IS ACTUALLY CANCELLED**, not merely ignored: a new
 * question and unmounting both abort the one in flight. A request nobody is
 * waiting for is still a paid request on its way to a provider.
 *
 * ---------------------------------------------------------------------------
 * WHAT LIVES WHERE, AND WHY THE SPLIT IS EXACTLY HERE
 * ---------------------------------------------------------------------------
 *
 * **THE VISIT LIVES ABOVE THE ROUTE** (`sol-session.tsx`): the exchanges, the
 * composer text and the exchange counter. That is what lets Brandon open a
 * source Capture, press Back, and find his answer still there instead of paying
 * to ask it again — the real-phone defect this split was made to fix.
 *
 * **THE REQUEST IN FLIGHT STAYS HERE, AND MUST.** A pending question, its abort
 * controller and a transport problem belong to the screen that is waiting.
 * Promoting `pendingQuestion` into the visit would be a bug with a very specific
 * shape: unmounting aborts the request, so a preserved "Sol is reading your
 * Captures…" would return on Back and **wait forever** for an answer that was
 * cancelled.
 *
 * **NO CONVERSATIONAL MEMORY ON THE CLIENT, ANYWHERE.** Earlier questions stay
 * VISIBLE, and that is all they are — nothing from an earlier exchange is sent
 * with a later one. What Sol remembers lives on the server (M11), and the
 * browser's whole share of it is **one identifier**: which conversation this
 * is. Since I-065 that identifier survives the app being killed, so a follow-up
 * after a relaunch still means what it says — but it is still only an id, and
 * every fact in the answer is still read live from Crewlink.
 *
 * **THE PROVIDER IS NEVER RETRIED FROM HERE.** Retry is a tap, never a loop.
 */

/** A transport-level problem, distinct from anything Sol itself answered. */
export type SolAskProblem = 'signed_out' | 'unavailable';

export interface SolAsk {
  /** Oldest first. The newest is the one the screen makes prominent. */
  readonly exchanges: readonly SolExchange[];
  /**
   * The STORED messages of the conversation being continued (M11 / D-041),
   * oldest first — drawn back from the server after a restart or when an
   * earlier conversation is reopened. Empty until loaded, and empty for a
   * brand-new conversation. **What was said, for a person to see**: nothing
   * here is sent with a question.
   */
  readonly restored: readonly SolRestoredMessage[];
  /** True while the stored messages are being read. */
  readonly restoring: boolean;
  /** The question currently in flight, or null. **Screen-local.** */
  readonly pendingQuestion: string | null;
  readonly problem: SolAskProblem | null;
  /** The composer's text. Part of the visit. */
  readonly draft: string;
  readonly setDraft: (value: string) => void;
  /** Submit the draft. False when it was rejected before any request was made. */
  readonly ask: () => boolean;
  /** Re-ask the newest question. **A new request, never a cached reply.** */
  readonly retryLast: () => boolean;
  /** End the visit deliberately. Nothing is kept. */
  readonly startOver: () => void;
}

export function useSolAsk(
  currentProjectId: string | null,
  openConversationId: string | null = null,
): SolAsk {
  const session = useSolSession();
  const { exchanges, draft, conversationId, restored, restoredFor } =
    session.visit(currentProjectId);
  // **DERIVED, NOT HELD.** Restoring is "the visit has a conversation whose
  // stored messages are not filed yet" — a fact about the visit, so there is no
  // second copy of it to fall out of step.
  const restoring = conversationId !== null && restoredFor !== conversationId;

  // **REOPENING AN EARLIER CONVERSATION** (M11 / D-041). The address named one;
  // if the visit is not already on it, point the visit at it. Runs when the id
  // changes, not on every render — the visit itself is the source of truth
  // afterwards.
  useEffect(() => {
    if (openConversationId === null) return;
    if (conversationId === openConversationId) return;
    session.openConversation(currentProjectId, openConversationId);
  }, [conversationId, currentProjectId, openConversationId, session]);

  // **THE RESTART PATH, AT MOUNT — NOT ONLY AT ASK TIME** (M11 / D-041). A
  // killed app comes back with an empty visit even though a conversation is
  // open on the server; recover the pointer now, so the stored bubbles can be
  // drawn before he types anything. Null in every case that is not this
  // owner's own current context, and null changes nothing.
  useEffect(() => {
    if (conversationId !== null || openConversationId !== null) return;
    let cancelled = false;
    void session.recoverConversationId(currentProjectId).then(() => {
      if (cancelled) return;
    });
    return () => {
      cancelled = true;
    };
    // Only when the visit has no conversation yet — the recovery adopts one
    // into the visit, which re-runs nothing here.
  }, [conversationId, currentProjectId, openConversationId, session]);

  // **DRAW THE STORED MESSAGES ONCE PER CONVERSATION.** Held in the visit so
  // navigating to a source Capture and back does not re-read them, and so the
  // live exchanges of this process are never duplicated by a second load.
  useEffect(() => {
    if (conversationId === null) return;
    if (restoredFor === conversationId) return;
    let cancelled = false;
    void loadSolConversationMessages({ conversationId })
      .then(({ messages }) => {
        if (cancelled) return;
        session.setRestored(currentProjectId, conversationId, messages);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        reportError(error, 'sol.restoreConversation');
        // Nothing restored, and nothing pretended: the visit simply shows the
        // new exchanges, exactly as it did before history existed.
        session.setRestored(currentProjectId, conversationId, []);
      });
    return () => {
      cancelled = true;
    };
  }, [conversationId, currentProjectId, restoredFor, session]);

  // **SCREEN-LOCAL, ON PURPOSE.** See the header: a preserved pending state
  // would wait forever for a request that unmounting already cancelled.
  const [pendingQuestion, setPendingQuestion] = useState<string | null>(null);
  const [problem, setProblem] = useState<SolAskProblem | null>(null);

  /** The newest id ever issued. Compared on arrival; never reset downwards. */
  const latest = useRef(0);
  const controller = useRef<AbortController | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      controller.current?.abort();
      controller.current = null;
    };
  }, []);

  const run = useCallback(
    (question: string, clearDraft: boolean): boolean => {
      const trimmed = question.trim();
      // **AN EMPTY QUESTION IS NOT A REQUEST.** The button is disabled too; this
      // is the half that also holds for Enter, for paste-then-submit, and for a
      // question that is nothing but spaces.
      if (trimmed.length === 0) return false;
      // **DUPLICATE SUBMIT, REFUSED.** The same words while the same words are
      // already in flight is a double tap, and paying twice for one question is
      // the entire cost of not checking. The guard runs before any await, so
      // two taps dispatched in the same tick cannot both start a request.
      //
      // **A DIFFERENT QUESTION IS NOT A DUPLICATE — IT SUPERSEDES.** He changed
      // his mind, and making him wait out an answer he no longer wants would be
      // the app's convenience, not his.
      if (trimmed === pendingQuestion) return false;

      const exchangeId = session.takeId(currentProjectId);
      const id = latest.current + 1;
      latest.current = id;

      controller.current?.abort();
      const active = new AbortController();
      controller.current = active;

      setPendingQuestion(trimmed);
      setProblem(null);
      if (clearDraft) session.setDraft(currentProjectId, '');

      // **THE RELAUNCH PATH (I-065).** After the app is killed, the visit is
      // empty and `conversationId` is null even though a conversation is very
      // much still open on the server — so before the request goes, ask the
      // device which one it was. It resolves to null in every case that is not
      // this owner's own current Project, and null is exactly what the code
      // below already did.
      //
      // **IT MUST HAPPEN BEFORE THE REQUEST, NOT AFTER IT.** Recovering on the
      // reply would send the pronoun with no conversation attached, which is
      // the very failure being fixed. The duplicate-submit guard and the
      // sequence id are both taken ABOVE, synchronously, so awaiting here
      // cannot let a second tap through or reorder two answers.
      void (async () => {
        const continuing =
          conversationId ?? (await session.recoverConversationId(currentProjectId));
        return askSol({
          question: trimmed,
          currentProjectId,
          // **THE ID ONLY.** What that conversation holds is the server's, read
          // as the signed-in caller. Start over cleared this, which is exactly
          // what makes the next question a new conversation.
          conversationId: continuing,
          signal: active.signal,
        });
      })().then((outcome) => {
        // **THE SEQUENCE CHECK, AND IT IS THE WHOLE POINT OF THIS HOOK.**
        if (!mounted.current || id !== latest.current) return;

        if (outcome.status === 'answered') {
          // **BEFORE THE EXCHANGE IS FILED**, so a follow-up typed the instant
          // the answer lands already carries the conversation it belongs to.
          if (outcome.conversationId !== null) {
            session.setConversationId(currentProjectId, outcome.conversationId);
          }
          session.addExchange(currentProjectId, {
            id: exchangeId,
            question: trimmed,
            presentation: presentSolResult(outcome.result, currentProjectId),
            // **VISIBLE BY CONTRACT.** What Sol noted on its own rides with
            // the exchange, so the chip and its Forget control survive Back.
            memoryNoted: outcome.memoryNoted ?? null,
          });
          setPendingQuestion(null);
          setProblem(null);
          return;
        }

        if (outcome.status === 'aborted') {
          // **STILL THE NEWEST, AND ABORTED ANYWAY.** A supersede was already
          // filtered out above, so this is a cancellation with nothing behind
          // it. Clearing matters: leaving the pending state set would leave
          // "Sol is reading your Captures…" on the screen forever.
          setPendingQuestion(null);
          return;
        }

        // Transport failure. **The question is NOT recorded as an exchange** —
        // nothing was answered — and his words go back into the composer
        // unless he has already started typing something else.
        setPendingQuestion(null);
        setProblem(outcome.status);
        // Decided against the LATEST draft — see `restoreDraftIfEmpty`.
        session.restoreDraftIfEmpty(currentProjectId, trimmed);
      });

      return true;
    },
    // **`conversationId` BELONGS HERE.** Without it the callback keeps the id
    // captured when it was created — null on the first render — and every
    // follow-up would begin a NEW conversation, so continuation would never
    // work from the phone however well the server behaved.
    [conversationId, currentProjectId, pendingQuestion, session],
  );

  const setDraft = useCallback(
    (value: string) => {
      session.setDraft(currentProjectId, value);
    },
    [currentProjectId, session],
  );

  const ask = useCallback(() => run(draft, true), [draft, run]);

  const retryLast = useCallback((): boolean => {
    const last = exchanges[exchanges.length - 1];
    if (last === undefined) return false;
    if (pendingQuestion !== null) return false;
    // **THE FAILED ANSWER IS REPLACED, NOT STACKED UNDER ITS OWN RETRY.** Two
    // entries for one question would read as two things Brandon asked. Dropped
    // BEFORE the new request starts, so the pending question never sits beneath
    // the answer it is replacing.
    session.dropNewest(currentProjectId);
    // The composer is not his retry text — it may already hold a new question.
    return run(last.question, false);
  }, [currentProjectId, exchanges, pendingQuestion, run, session]);

  const startOver = useCallback(() => {
    // **THE HONEST END OF A VISIT.** A visit that survives navigation needs a
    // visible way to end it, or "it is still here" quietly becomes "it is kept".
    controller.current?.abort();
    controller.current = null;
    latest.current += 1;
    setPendingQuestion(null);
    setProblem(null);
    session.clear(currentProjectId);
  }, [currentProjectId, session]);

  return {
    exchanges,
    restored: restored ?? [],
    restoring,
    pendingQuestion,
    problem,
    draft,
    setDraft,
    ask,
    retryLast,
    startOver,
  };
}
